import { Inject, Injectable, Optional } from '@angular/core';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { IWorkoutApi } from '../api/interfaces/workout-abstract.api';
import { WorkoutApi } from '../api/workout.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import {
  AbstractPaginableFacade,
  ItemsPaginableState,
} from './abstract-paginable.facade';
import { Workout } from '@kinomap-dev/ng-ui-lib';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'app/services/toaster.service';

// 3 Minutes
const CACHE_VALIDITY: number = 180000;

@Injectable({
  providedIn: 'root',
})
export class WorkoutsFacade extends AbstractPaginableFacade<
  Workout,
  ItemsPaginableState<Workout>
> {
  public readonly workouts$: Observable<Workout[]>;
  public readonly total$: Observable<number>;
  public readonly loading$: Observable<boolean>;

  constructor(
    private readonly workoutApi: IWorkoutApi,
    @Inject(NEVER_TOKEN) @Optional() private userId: number,
    private toasterService: ToasterService,
    private translate: TranslateService
  ) {
    super(
      {
        loading: false,
        total: null,
        items: [],
        lastRequestedIndex: 0,
        lastLoadingTime: 0,
      },
      CACHE_VALIDITY
    );
    this.workouts$ = this.state$.pipe(
      map((state) => state.items),
      distinctUntilChanged()
    );
    this.total$ = this.state$.pipe(
      map((state) => state.total),
      distinctUntilChanged()
    );
    this.loading$ = this.state$.pipe(
      map((state) => state.loading),
      distinctUntilChanged()
    );
  }

  loadFromApi(start: number, end: number) {
    // Do not reload if a page is currently loading
    if (end <= 0 || this.isTotallyLoaded() || this.state.loading) {
      return;
    }

    const { page, pageSize } = this.computePagesAndSizeFromInterval(start, end);
    const offset: number = (page - 1) * pageSize;

    this.setState({
      loading: true,
      lastRequestedIndex: end,
    });

    this.workoutApi.getWorkouts(page, pageSize, this.userId).subscribe({
      next: ({ data: newWorkouts, meta }) => {
        const allWorkouts = this.mergeNewItemsToOldOnes(newWorkouts, offset);
        this.cacheValidityTime = Date.now() + this.cacheValidity;
        this.setState({
          loading: false,
          items: allWorkouts,
          total: meta.total,
        });
        // TODO => Run following data in queue
      },
      error: (err) => {
        console.error(err);
        this.setState({
          loading: false,
        });
      },
    });
  }

  deleteWorkout(workoutId: number): void {
    (this.workoutApi as WorkoutApi).deleteWorkout(workoutId).subscribe({
      next: () => {
        const success: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-success'
        );
        this.toasterService.success(null, success);
        const workoutIndex: number = this.state.items.findIndex(
          (workout: Workout) => workout.id === workoutId
        );

        if (workoutIndex === -1) {
          return;
        }

        const newWorkouts: Workout[] = [...this.state.items];
        newWorkouts.splice(workoutIndex, 1);
        this.setState({
          items: newWorkouts,
          total: this.state.total - 1,
          lastRequestedIndex: this.state.lastRequestedIndex - 1,
        });
      },

      error: () => {
        const error: string = this.translate.instant(
          'dashboard.manage-content.popup.confirm.delete-content-error'
        );
        this.toasterService.warning(null, error);
      },
    });
  }
}
